import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Input, Text } from "@chakra-ui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";

export interface CheckboxFilterOption {
  label: string;
  value: string | number;
}

// 옵션이 이 개수 이상이면 검색창을 제공한다.
const SEARCHABLE_THRESHOLD = 5;

// 옵션 카드 높이 — 패딩 8×2 + 라벨 줄높이 18 + 보더(선택 시 1px×2)가 들어가는 값으로 고정한다.
// 행을 실측하면 보더 두께·디스플레이 배율에 따라 추정값과 1~2px씩 어긋나, 새 행이 측정될 때마다
// 전체 높이가 바뀌고 스크롤 위치가 보정되어 스크롤바가 튄다. 고정 높이로 측정 자체를 없앤다.
const OPTION_HEIGHT = 36;
const OPTION_GAP = 8;

interface Props {
  options: CheckboxFilterOption[];
  selectedValues: (string | number)[];
  onToggle: (value: string | number) => void;
  maxHeight?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
}

interface OptionRowProps {
  option: CheckboxFilterOption;
  // 가상 리스트 안의 세로 위치(px)
  start: number;
  isSelected: boolean;
  onToggle: (value: string | number) => void;
}

// 스크롤마다 부모가 다시 그려져도 이미 보이던 행은 건너뛰도록 memo로 끊는다
// — 고정 높이라 같은 행의 props(위치·선택 여부)는 스크롤 중 바뀌지 않는다.
const CheckboxFilterOptionRow = React.memo(function CheckboxFilterOptionRow({
  option,
  start,
  isSelected,
  onToggle,
}: OptionRowProps) {
  return (
    // 카드 전체를 클릭 대상으로 하고, AppCheckBox에는 onChange를 넘기지 않아
    // 체크박스 클릭이 카드 onClick으로 버블되어 한 번만 토글되게 한다.
    // 위치는 행마다 값이 달라 Chakra prop으로 넘기면 emotion이 값마다 CSS 클래스를 새로 만들므로 style로 준다.
    <Flex
      position={"absolute"}
      top={0}
      left={0}
      width={"100%"}
      height={`${OPTION_HEIGHT}px`}
      style={{ transform: `translateY(${start}px)` }}
      alignItems={"center"}
      gap={"8px"}
      padding={"8px 12px"}
      cursor={"pointer"}
      border={isSelected ? "1px solid #1272EF" : "0.5px solid #D4D4D1"}
      borderRadius={"8px"}
      background={isSelected ? "#F0F7FF" : "#FFFFFF"}
      onClick={() => onToggle(option.value)}
      _hover={{ borderColor: "#1272EF" }}
    >
      <AppCheckBox size={"small"} isChecked={isSelected} />
      {/* 고정 높이를 넘지 않게 라벨은 한 줄로 자르고, 전체 이름은 호버로 보여준다. */}
      <Text
        fontSize={"12px"}
        noOfLines={1}
        minWidth={0}
        wordBreak={"break-all"}
        title={option.label}
      >
        {option.label}
      </Text>
    </Flex>
  );
});

function CheckboxFilterGroup({
  options,
  selectedValues,
  onToggle,
  maxHeight = "200px",
  searchPlaceholder = "",
  noResultsText = "",
}: Props) {
  const [query, setQuery] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const searchable = options.length >= SEARCHABLE_THRESHOLD;

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(query.trim().toLowerCase())
      )
    : options;

  const selectedValueSet = useMemo(
    () => new Set(selectedValues),
    [selectedValues]
  );

  // 브랜드처럼 옵션이 수천 개인 그룹을 전부 그리면 패널을 펼칠 때 마운트·레이아웃이 오래 걸린다.
  // 스크롤 영역에 보이는 옵션만 그린다.
  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => OPTION_HEIGHT,
    gap: OPTION_GAP,
    overscan: 8,
  });

  // 검색 결과가 줄었을 때 이전 스크롤 위치(빈 영역)에 머무르지 않게 맨 위로 올린다.
  useEffect(() => {
    virtualizer.scrollToOffset(0);
  }, [query, virtualizer]);

  return (
    <Flex flexDirection={"column"} gap={"8px"}>
      {searchable && (
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          fontSize={"12px"}
          height={"28px"}
          borderColor={"#D4D4D1"}
          _focusVisible={{ borderColor: "#1272EF", boxShadow: "none" }}
        />
      )}
      <Box ref={scrollRef} maxHeight={maxHeight} overflowY={"auto"}>
        {filteredOptions.length === 0 ? (
          <Text
            textStyle={"body-sm-medium"}
            color={"#8F8F8C"}
            padding={"4px 0"}
          >
            {noResultsText}
          </Text>
        ) : (
          <Box
            position={"relative"}
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const option = filteredOptions[virtualItem.index];
              return (
                <CheckboxFilterOptionRow
                  key={option.value}
                  option={option}
                  start={virtualItem.start}
                  isSelected={selectedValueSet.has(option.value)}
                  onToggle={onToggle}
                />
              );
            })}
          </Box>
        )}
      </Box>
    </Flex>
  );
}

export default CheckboxFilterGroup;
